import { useState, useCallback, useEffect, useRef } from 'react';

export interface Area {
  id: number;
  area_name: string;
  area_pro: string | null;
  area_con: string | null;
  area_overall: string | null;
  created_at: string;
  updated_at: string;
  home_id: string;
}

interface UseHomeAreasOptions {
  homeIdOverride?: string;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  showNotifications?: boolean; // New option to control notification behavior
}

export function useHomeAreas(options?: UseHomeAreasOptions) {
  const [areas, setAreas] = useState<Area[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number>(0);
  const homeId = options?.homeIdOverride || '';
  const showNotifications = options?.showNotifications ?? false;
  
  // Use refs for callback functions to avoid dependency changes
  const onSuccessRef = useRef(options?.onSuccess);
  const onErrorRef = useRef(options?.onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = options?.onSuccess;
    onErrorRef.current = options?.onError;
  }, [options?.onSuccess, options?.onError]);

  // Fetch all areas for a home
  const fetchAreas = useCallback(async () => {
    if (!homeId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(`/api/homes/${homeId}/areas`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch areas: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setAreas(data.areas);
        setLastUpdated(Date.now());
        
        // Log success to console instead of showing notification
        console.log('Areas refreshed successfully');
        
        // Only show UI notification if explicitly requested
        if (showNotifications && onSuccessRef.current) {
          onSuccessRef.current('Areas refreshed successfully');
        }
      } else {
        throw new Error(data.error || 'Failed to fetch areas');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      
      // Always log errors to console
      console.error('Error fetching areas:', err);
      
      // Only show UI notification for errors if explicitly requested
      if (showNotifications && onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [homeId, showNotifications]); // Only depend on homeId and notification preference

  // Update area details - for explicit user actions, we DO want notifications
  const updateAreaDetails = useCallback(async (
    areaName: string, 
    updates: { area_pro?: string; area_con?: string; area_overall?: string }
  ) => {
    if (!homeId || !areaName) return { success: false };
    
    try {
      const response = await fetch(`/api/homes/${homeId}/areas/${encodeURIComponent(areaName)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update local state with the changes
        setAreas(prevAreas => prevAreas.map(area => {
          if (area.area_name === areaName) {
            return { ...area, ...updates };
          }
          return area;
        }));
        setLastUpdated(Date.now());
        
        console.log(`Updated details for ${areaName}`);
        
        // For explicit updates, always show notification
        if (onSuccessRef.current) {
          onSuccessRef.current(`Updated details for ${areaName}`);
        }
        return { success: true };
      } else {
        throw new Error(data.error || `Failed to update ${areaName}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error(`Error updating area ${areaName}:`, err);
      
      // For explicit updates, always show error notification
      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }
      return { success: false, error: errorMessage };
    }
  }, [homeId]); // Only depend on homeId

  // Initial fetch on mount - using a ref to ensure it only runs once
  const initialFetchRef = useRef(false);
  
  useEffect(() => {
    if (homeId && !initialFetchRef.current) {
      initialFetchRef.current = true;
      fetchAreas();
    }
  }, [homeId, fetchAreas]);

  return {
    areas,
    isLoading,
    error,
    fetchAreas,
    updateAreaDetails,
    setError,
    lastUpdated
  };
}